import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { cloneDeep } from 'lodash-es';
import { BUSY } from '@common/dialogs/intersection-dialog/const/timeline.const';
import moment from 'moment';
import { isDayOfDatesEqual, setTimeZone } from '@common/dialogs/intersection-dialog/helpers/date.helpers';
import {
  BusyTimeline,
  TimelineEvent,
  TotalTimeline,
  StatusOfEvent,
  Timeline
} from '@common/dialogs/planning-dialog/modules/planning-intersection/modules/busy-timeline/types';
import { addHours, differenceInMinutes, endOfDay, getHours, getMinutes, startOfDay } from 'date-fns';

@Component({
  selector: 'com-busy-timeline',
  templateUrl: 'busy-timeline.component.html',
  styleUrls: ['busy-timeline.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BusyTimelineComponent implements OnInit, OnChanges {
  @Input() forecastEventStart?: string;
  @Input() forecastEventEnd?: string;
  @Input() timelineEntities: BusyTimeline[] = [];
  @Input() isLoading: boolean;
  @Input() cellSize = 60;
  @Input() itemListWidth = 607;

  private eventTimeHours: number[] = [];
  private colorOfEventTime = '';
  private colorOfBusyTime = 'com-busy-gradient';
  private headerStep = 1;

  public timelineStart = 8;
  public timelineEnd = 20;
  public timelineSize = 10;

  public windowWidth$ = new BehaviorSubject<number>(100);
  public windowHeight$ = new BehaviorSubject<number>(100);
  public windowLeft$ = new BehaviorSubject<number>(0);

  public scale$ = new BehaviorSubject<Timeline[]>([]);
  public eventTimeline$ = new BehaviorSubject<TotalTimeline[]>([]);
  public timelineGrid$ = new BehaviorSubject<TotalTimeline[]>([]);

  ngOnInit(): void {
    this.initTimelineStartAndEnd();
    this.initEventTime();
    this.initScale();
    this.initTimeline(this.timelineEntities);
    this.timelineWindowCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('forecastEventStart' in changes || 'forecastEventEnd' in changes || 'rooms' in changes) {
      this.initTimelineStartAndEnd();
      if (this.forecastEventStart && this.forecastEventEnd) {
        this.initEventTime();
        this.initTimelineWindow(this.forecastEventStart, this.forecastEventEnd);
      }
      this.initScale();
      this.initTimeline(this.timelineEntities);
    }
    if ('timelineEntities' in changes) {
      this.initTimeline(this.timelineEntities);
      this.timelineWindowCheck();
    }
  }

  private timelineWindowCheck(): void {
    if (this.forecastEventStart && this.forecastEventEnd) {
      this.initTimelineWindow(this.forecastEventStart, this.forecastEventEnd);
    }
  }

  private initEventTime(): void {
    const [startHour, endHour] = this.getStartHourAndEndHour(this.forecastEventStart, this.forecastEventEnd);

    if (startHour !== null && endHour !== null) {
      this.eventTimeHours = [startHour, endHour];
    }
  }

  private initTimelineWindow(start: string, end: string): void {
    this.windowHeight$.next(this.timelineEntities.length * 60 + 48);
    this.windowWidth$.next(differenceInMinutes(new Date(end), new Date(start)));
    const date = new Date(this.forecastEventStart);
    this.windowLeft$.next(getHours(date) * 60 + getMinutes(date) - this.timelineStart * 60);
  }

  private initScale(): void {
    const scale = [];
    for (let i = this.timelineStart; i < this.timelineEnd; i = i + this.headerStep) {
      let valueName = null;
      if (Number.isInteger(i)) {
        valueName = String(i);
        valueName = valueName.length < 2 ? '0' + valueName : valueName;
      }

      scale.push({
        value: i,
        valueName,
        status: StatusOfEvent.empty,
        color: null
      });
    }
    this.scale$.next(scale);
  }

  private createTimelineEvents(member: BusyTimeline): TimelineEvent[] {
    const events: TimelineEvent[] = [];

    for (const event of member.events) {
      const [startHour, endHour] = this.getStartHourAndEndHour(event.timeStart, event.timeEnd);

      if (startHour !== null && endHour !== null) {
        events.push({
          ...event,
          timelineStart: this.checkGraphStep(startHour),
          timelineEnd: this.checkGraphStep(endHour)
        });
      }
    }

    return [...events, this.getForecastEvent()];
  }

  private checkBorders(event: TimelineEvent): TimelineEvent {
    if (event.timelineStart < this.timelineStart) {
      event.timelineStart = this.timelineStart;
    }

    if (event.timelineEnd > this.timelineEnd) {
      event.timelineEnd = this.timelineEnd;
    }

    return event;
  }

  private createTimeline(events: TimelineEvent[]): Timeline[] {
    const timelineEvents: Timeline[] = [];
    events.forEach((event) => {
      const lastTimeline = timelineEvents[timelineEvents.length - 1];
      if (!lastTimeline) {
        timelineEvents.push(this.getEvent(event));
        return;
      }

      if (lastTimeline?.value[0] > event.timelineStart && lastTimeline?.value[1] < event.timelineEnd) {
        return;
      }

      if (event.timelineStart > lastTimeline?.value[0] && event.timelineEnd < lastTimeline?.value[1]) {
        timelineEvents.slice(-1);
      }

      timelineEvents.push(this.getEvent(event));
    });
    return timelineEvents;
  }

  private initTimeline(statuses: BusyTimeline[]): void {
    this.initGrid(statuses);
    this.initEventOnTimeline(statuses);
  }

  private initEventOnTimeline(statuses: BusyTimeline[]): void {
    this.eventTimeline$.next(
      statuses.map((member) => {
        return {
          ...member,
          timeline: this.mergeCells(
            this.createTimeline(
              this.createTimelineEvents(member)
                .map((event) => this.checkBorders(event))
                .filter((event) => event.timelineEnd > event.timelineStart)
                .sort((a, b) => a.timelineStart - b.timelineStart)
            )
          )
        };
      })
    );
  }

  private initGrid(statuses: BusyTimeline[]): void {
    this.timelineGrid$.next(
      cloneDeep(statuses).map((member) => {
        member.events = [];
        return {
          ...member,
          timeline: this.initEmptyTimeline()
        };
      })
    );
  }

  private mergeCells(timelineEvents: Timeline[]): Timeline[] {
    const timeMap = new Map<number, Date>();
    const timelineMap = this.createTimelineMap();
    const eventTime = timelineEvents.find((c) => c.status === StatusOfEvent.eventTime);
    const [startPlanning, endPlanning] = this.initEventWindow();

    timelineEvents.map(({ status, value, event }) => {
      if (status === StatusOfEvent.userEvent) {
        const [start, end] = value;
        timeMap.set(start, setTimeZone(event.timeStart));
        timeMap.set(end, setTimeZone(event.timeEnd));

        for (let i = start; i <= end; i += 0.02) {
          i = parseFloat(i.toFixed(2));
          timelineMap.set(i, !(i === startPlanning || i === endPlanning));
        }
      }
    });

    return this.calculateMergedTimeline(timelineMap, eventTime, timeMap, timelineEvents);
  }

  private checkGraphStep(value: number): number {
    const temp = value.toString();
    const lastDigit = parseInt(temp[temp.length - 1]);
    if (lastDigit % 2 !== 0 && temp.split('.')[1]?.length > 1) {
      return parseFloat((value - 0.01).toFixed(2));
    }
    return value;
  }

  private createTimelineMap(): Map<number, boolean> {
    const timelineMap = new Map<number, boolean>();
    for (let i = 0; i <= 24; i = i += 0.02) {
      timelineMap.set(parseFloat(i.toFixed(2)), false);
    }
    return timelineMap;
  }

  private calculateMergedTimeline(
    timelineMap: Map<number, boolean>,
    eventTime: Timeline,
    timeMap: Map<number, Date>,
    timelineEvents: Timeline[]
  ): Timeline[] {
    const result: Timeline[] = [eventTime];
    const [startPlanning, endPlanning] = this.initEventWindow();
    let startTimeStep = null;
    let endTimeStep = null;

    for (const [k, value] of timelineMap) {
      const key = parseFloat(k.toFixed(2));

      if (!startTimeStep) {
        startTimeStep = key;
      }

      if (value) {
        endTimeStep = key;
      } else {
        if (startTimeStep && endTimeStep) {
          if (startTimeStep === 0.02) startTimeStep = 0;
          result.push({
            value: [startTimeStep, endTimeStep],
            valueName: BUSY,
            status: StatusOfEvent.userEvent,
            color: this.colorOfBusyTime,
            startTime: timeMap.get(startTimeStep),
            endTime: timeMap.get(endTimeStep),
            events: this.getMergedCellEvents(timelineEvents, startTimeStep, endTimeStep) || [],
            beyondPlanningWindow: !(startTimeStep <= endPlanning && startPlanning <= endTimeStep)
          });
        }
        startTimeStep = null;
        endTimeStep = null;
      }
    }
    return result;
  }

  private getMergedCellEvents(timelineEvents: Timeline[], start: number, end: number): TimelineEvent[] {
    return timelineEvents
      .map((t) => {
        const [cellStart, cellEnd] = t.value;
        if ((cellStart >= start && cellStart <= end) || (cellEnd >= start && cellEnd <= end)) {
          return t.event;
        }

        return null;
      })
      .filter(Boolean);
  }

  public changeIntervalAdd(): void {
    this.timelineStart = this.timelineStart + 1;
    this.timelineEnd = this.timelineEnd + 1;
    this.initScale();
    this.initTimeline(this.timelineEntities);
    this.timelineWindowCheck();
  }

  public changeIntervalSubtract(): void {
    this.timelineStart = this.timelineStart - 1;
    this.timelineEnd = this.timelineEnd - 1;
    this.initScale();
    this.initTimeline(this.timelineEntities);
    this.timelineWindowCheck();
  }

  private getStartHourAndEndHour(
    startTime: string | Date,
    endTime: string | Date
  ): [startHour: number | null, endHour: number | null] {
    let startHour = Number(moment(setTimeZone(startTime)).format('HH'));
    let endHour = Number(moment(setTimeZone(endTime)).format('HH'));
    const startMinutes = Number(moment(setTimeZone(startTime)).minutes());
    const endMinutes = Number(moment(setTimeZone(endTime)).minutes());

    startHour += +(startMinutes / 60).toFixed(2);
    endHour += +(endMinutes / 60).toFixed(2);

    if (startHour === endHour) {
      startHour = null;
      endHour = null;
    }
    if (startHour > endHour) {
      if (isDayOfDatesEqual(new Date(), startTime)) {
        endHour = 24;
      } else {
        startHour = 0;
      }
    }
    return [startHour, endHour];
  }

  private initTimelineStartAndEnd(): void {
    const startHour = Number(moment(this.forecastEventStart || new Date()).format('HH'));
    const endHour = Number(moment(this.forecastEventEnd || addHours(new Date(), 1)).format('HH'));
    const sizeCorrection = 11 - this.timelineSize;

    this.timelineStart = startHour - 4;
    this.timelineEnd = endHour + 8;

    if (this.timelineStart + this.timelineEnd > 12) {
      this.timelineStart = startHour - 1;
      this.timelineEnd = startHour + 10 - sizeCorrection;
    }

    if (this.timelineStart <= 0) {
      this.timelineStart = 0;
      this.timelineEnd = 12 - sizeCorrection;
    }

    if (this.timelineEnd > 24) {
      this.timelineEnd = 24;
      this.timelineStart = 13 + sizeCorrection;
    }
  }

  private initEmptyTimeline(): Timeline[] {
    const beforeTimelines: Timeline[] = [];
    let beforeTime = this.timelineStart;
    while (beforeTime < this.timelineEnd) {
      const startHour = beforeTime;
      beforeTime = beforeTime + 1 < this.timelineEnd ? beforeTime + 1 : this.timelineEnd;

      beforeTimelines.push({
        value: [startHour, beforeTime],
        status: StatusOfEvent.empty,
        color: null,
        startTime: null,
        endTime: null
      });
    }

    return beforeTimelines;
  }

  private initEventWindow(): [start: number, end: number] {
    if (this.forecastEventStart && this.forecastEventEnd) {
      const [startHour, endHour] = this.getStartHourAndEndHour(
        this.forecastEventStart,
        this.forecastEventEnd
      );
      return [this.checkGraphStep(startHour), this.checkGraphStep(endHour)];
    }

    const [start, end] = this.initWindowWithoutForecast();
    const [startHour, endHour] = this.getStartHourAndEndHour(start, end);

    return [this.checkGraphStep(startHour), this.checkGraphStep(endHour)];
  }

  private initWindowWithoutForecast(): [start: Date, end: Date] {
    return [startOfDay(new Date()), endOfDay(new Date())];
  }

  private getEvent(event: TimelineEvent): Timeline {
    if (event.eventId) {
      return {
        value: [event.timelineStart, event.timelineEnd],
        valueName: 'Занят',
        status: StatusOfEvent.userEvent,
        color: this.colorOfBusyTime,
        startTime: null,
        endTime: null,
        event
      };
    }
    return {
      value: [event.timelineStart, event.timelineEnd],
      status: StatusOfEvent.eventTime,
      color: this.colorOfEventTime,
      startTime: null,
      endTime: null
    };
  }

  private getForecastEvent(): TimelineEvent {
    return {
      timeEnd: null,
      timeStart: null,
      timelineStart: this.eventTimeHours[0],
      timelineEnd: this.eventTimeHours[1],
      eventId: null,
      eventName: null,
      roleId: null,
      roleName: null
    };
  }
}
